/**
 * UnitMembersSection - Gestão de membros (gerente/membros) de uma unidade
 *
 * Molde: `src/modules/teams/components/sections/TeamMembersSection.tsx`.
 * Papéis da unidade (`UnitUserRole`): 'manager' (Gerente) | 'member' (Membro).
 */

import React, { useState } from 'react';
import { Button } from '@/shared/components/ui/Button';
import { Avatar, AvatarFallback, AvatarImage } from '@/shared/components/ui/avatar';
import { Badge } from '@/shared/components/ui/badge';
import { useUnitUsers, useAddUnitUser, useRemoveUnitUser } from '../hooks/useUnitUsers';
import { useUsersDropdown } from '@/modules/users/hooks/useUsers';
import { Icon } from '@/shared/icons';
import type { UnitUserRole } from '@troia-v3/types';
import {
  DeleteConfirmationModal,
  Modal,
  ModalHeader,
  ModalContent,
  ModalFooter,
  Select,
  type SelectOption,
} from '@/shared/components/ui';

interface UnitMembersSectionProps {
  unitId: string;
}

const ROLE_OPTIONS: SelectOption[] = [
  { value: 'manager', label: 'Gerente' },
  { value: 'member', label: 'Membro' },
];

const ROLE_LABELS: Record<UnitUserRole, string> = {
  manager: 'Gerente',
  member: 'Membro',
};

export const UnitMembersSection: React.FC<UnitMembersSectionProps> = ({ unitId }) => {
  const { data: members = [], isLoading } = useUnitUsers(unitId);
  const { data: availableUsers = [] } = useUsersDropdown({ status: 'active' });
  const addMutation = useAddUnitUser();
  const removeMutation = useRemoveUnitUser();

  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedRole, setSelectedRole] = useState<UnitUserRole>('member');
  const [memberToRemove, setMemberToRemove] = useState<{ userId: string; name: string } | null>(null);

  const handleAddMember = async () => {
    if (!selectedUserId) return;

    try {
      await addMutation.mutateAsync({
        unitId,
        data: { userId: selectedUserId, role: selectedRole },
      });
      setShowAddModal(false);
      setSelectedUserId('');
      setSelectedRole('member');
    } catch (error) {
      console.error('Erro ao adicionar membro da unidade:', error);
    }
  };

  const handleConfirmRemove = async () => {
    if (!memberToRemove) return;

    try {
      await removeMutation.mutateAsync({ unitId, userId: memberToRemove.userId });
      setMemberToRemove(null);
    } catch (error) {
      console.error('Erro ao remover membro da unidade:', error);
    }
  };

  const getInitials = (name?: string) => {
    if (!name) return '??';
    return name.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2);
  };

  // Cor da pill por papel: gerente=lilás (default), membro=neutro
  const roleBadgeVariant: Record<UnitUserRole, 'default' | 'secondary'> = {
    manager: 'default',
    member: 'secondary',
  };

  // Filter out users that are already members
  const memberIds = members.map((m) => m.userId);
  const availableUsersFiltered = availableUsers.filter((u) => !memberIds.includes(u.id));

  return (
    <div
      className="rounded-xl border overflow-hidden"
      style={{
        backgroundColor: 'var(--theme-card-background)',
        borderColor: 'var(--theme-card-border)',
      }}
    >
      {/* Section Header (.card-h) */}
      <div
        className="flex items-center justify-between gap-3 px-[18px] py-[15px] border-b"
        style={{ borderColor: 'var(--theme-card-border)' }}
      >
        <div className="flex items-center gap-3 min-w-0">
          <div
            className="w-[34px] h-[34px] rounded-[9px] flex items-center justify-center flex-shrink-0"
            style={{
              backgroundColor: 'var(--theme-primary-background)',
              color: 'var(--theme-primary)',
            }}
          >
            <Icon name="users" className="w-5 h-5" />
          </div>
          <div className="min-w-0">
            <h2 className="text-[15px] font-bold" style={{ color: 'var(--theme-card-text)' }}>
              Equipe da Unidade
            </h2>
            <p className="text-xs" style={{ color: 'var(--theme-muted-text)' }}>
              Gerente e membros responsáveis pela unidade
            </p>
          </div>
        </div>
        <Button onClick={() => setShowAddModal(true)} size="sm" variant="secondary" data-testid="unit-members-add">
          <Icon name="user-plus" className="w-4 h-4 mr-2" />
          <span className="hidden sm:inline">Adicionar Membro</span>
        </Button>
      </div>

      {/* Members list (.card-b) */}
      <div className="p-[18px]">
        {isLoading ? (
          <p className="text-sm" style={{ color: 'var(--theme-card-muted)' }}>Carregando...</p>
        ) : members.length === 0 ? (
          <p className="text-sm" style={{ color: 'var(--theme-card-muted)' }}>Nenhum membro atribuído</p>
        ) : (
          <div>
            {members.map((member, index) => (
              <div
                key={member.id}
                className={`flex items-center gap-3 py-3${index > 0 ? ' border-t' : ''}`}
                style={index > 0 ? { borderColor: 'var(--theme-card-border)' } : {}}
                data-testid={`unit-member-row-${member.userId}`}
              >
                <Avatar className="h-9 w-9" isSquircle>
                  <AvatarImage src={member.userAvatar} isSquircle />
                  <AvatarFallback isSquircle>{getInitials(member.userName)}</AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm leading-tight truncate" style={{ color: 'var(--theme-card-text)' }}>
                    {member.userName || member.userEmail || 'Usuário sem nome'}
                  </p>
                  {member.userEmail && (
                    <p className="text-xs truncate" style={{ color: 'var(--theme-muted-text)' }}>
                      {member.userEmail}
                    </p>
                  )}
                </div>
                <Badge variant={roleBadgeVariant[member.role]} className="text-xs">
                  {ROLE_LABELS[member.role]}
                </Badge>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setMemberToRemove({ userId: member.userId, name: member.userName || member.userEmail || 'este membro' })}
                  disabled={removeMutation.isPending}
                  data-testid={`unit-member-remove-${member.userId}`}
                >
                  <Icon name="trash" className="w-4 h-4" style={{ color: 'var(--theme-error-color)' }} />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add Member Modal (shared, claro-fixo) */}
      <Modal isOpen={showAddModal} onClose={() => setShowAddModal(false)} maxWidth="md">
        <ModalHeader onClose={() => setShowAddModal(false)}>Adicionar Membro</ModalHeader>
        <ModalContent>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: 'var(--theme-text)' }}>
                Usuário
              </label>
              <Select
                value={selectedUserId}
                onChange={setSelectedUserId}
                placeholder="Selecione um usuário"
                options={availableUsersFiltered.map((u) => ({ value: u.id, label: u.name }))}
                data-testid="unit-member-user"
              />
              <p className="text-xs mt-1" style={{ color: 'var(--theme-muted-text)' }}>
                Apenas usuários ativos que ainda não fazem parte da unidade
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: 'var(--theme-text)' }}>
                Papel
              </label>
              <Select
                value={selectedRole}
                onChange={(v) => setSelectedRole(v as UnitUserRole)}
                options={ROLE_OPTIONS}
                data-testid="unit-member-role"
              />
            </div>
          </div>
        </ModalContent>
        <ModalFooter>
          <Button onClick={() => setShowAddModal(false)} variant="secondary" data-testid="unit-member-cancel">
            Cancelar
          </Button>
          <Button
            onClick={handleAddMember}
            disabled={!selectedUserId || addMutation.isPending}
            data-testid="unit-member-confirm"
          >
            Adicionar
          </Button>
        </ModalFooter>
      </Modal>

      {/* Remove Member Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={memberToRemove !== null}
        onClose={() => setMemberToRemove(null)}
        onConfirm={handleConfirmRemove}
        title="Remover membro"
        message={`Tem certeza que deseja remover ${memberToRemove?.name ?? ''} da unidade?`}
        confirmButtonText="Remover"
        isLoading={removeMutation.isPending}
        loadingText="Removendo..."
      />
    </div>
  );
};

UnitMembersSection.displayName = 'UnitMembersSection';
